/**
 * Destination-gas manager.
 *
 * Owner decision (2026-08-10): gas on Robinhood Chain is paid FROM the
 * round-up batch — it reduces the amount invested and is never charged on
 * top of the user's round-up. The invariant `reserve debit == Σ round-ups`
 * therefore holds even across gas refills.
 *
 * When the wallet's 4663 ETH balance is below DEST_GAS.minWei, a slice of
 * the batch (capped at DEST_GAS.maxBatchShare) is routed
 * USDC.e (Gnosis) → ETH (4663) before bridging the remainder. One ~£1.60
 * refill covers roughly 40 swaps at observed gas prices, so most batches
 * spend nothing on gas.
 */
import { DEST_GAS, GNOSIS_CHAIN, GNOSIS_GAS, LIFI, RESERVE_TOKEN, ROBINHOOD_CHAIN } from "./config";
import { gnosisPublicClient, robinhoodPublicClient } from "./chains";
import {
  getLifiQuote,
  sendLifiTransaction,
  sendRecoverableLifiTransaction,
  waitLifiDone,
  type PreparedLifiTransaction,
} from "./lifi";
import type { LocalWallet } from "./wallet";

/** LiFi rejects sub-$0.50 transfers; don't attempt smaller refills. */
const MIN_REFILL_UNITS = 500_000n;

export interface GasPrepResult {
  /** USDC.e units taken from the batch for gas (0n when tank was full). */
  gasSpentUnits: bigint;
  txHash?: `0x${string}`;
}

export async function destEthBalance(wallet: LocalWallet): Promise<bigint> {
  return robinhoodPublicClient().getBalance({ address: wallet.address });
}

/**
 * Ensure the wallet can pay for the upcoming swap on 4663. Returns how much
 * of the batch was converted to gas so the engine can bridge the remainder.
 */
export async function prepareDestinationGas(
  wallet: LocalWallet,
  batchUnits: bigint,
): Promise<GasPrepResult> {
  const balance = await destEthBalance(wallet);
  if (balance >= DEST_GAS.minWei) return { gasSpentUnits: 0n };

  const maxShare = (batchUnits * BigInt(Math.round(DEST_GAS.maxBatchShare * 100))) / 100n;
  const refill = DEST_GAS.topUpUnits < maxShare ? DEST_GAS.topUpUnits : maxShare;
  if (refill < MIN_REFILL_UNITS) {
    // Batch too small to buy meaningful gas — let the batch keep queueing
    // rather than burn it all on fees.
    throw new Error(
      "Robinhood Chain gas is empty and this batch is too small to refill it. " +
        "It will retry once more round-ups accumulate.",
    );
  }

  const txHash = await refillDestGas(wallet, refill);
  return { gasSpentUnits: refill, txHash };
}

/** Route `units` of reserve USDC.e (Gnosis) into ETH on 4663. */
async function refillDestGas(wallet: LocalWallet, units: bigint): Promise<`0x${string}`> {
  const quote = await getLifiQuote({
    fromChain: GNOSIS_CHAIN.id,
    toChain: ROBINHOOD_CHAIN.id,
    fromToken: RESERVE_TOKEN.address,
    toToken: "native",
    fromAmount: units,
    fromAddress: wallet.address,
    slippage: LIFI.bridgeSlippage,
  });
  const txHash = await sendLifiTransaction(wallet.account, quote);
  await waitLifiDone(txHash, GNOSIS_CHAIN.id, ROBINHOOD_CHAIN.id);
  return txHash;
}

/**
 * Keep the wallet's Gnosis-side xDAI topped up FROM the reserve, so after
 * the one-time sponsored bootstrap the user never thinks about gas again.
 * Called after each reserve top-up (fresh USDC.e just arrived). Best-effort:
 * callers treat failure as non-fatal — the next top-up retries.
 */
export async function ensureGnosisGas(
  wallet: LocalWallet,
  onProgress?: (label: string) => void,
): Promise<void> {
  const balance = await gnosisPublicClient().getBalance({ address: wallet.address });
  if (balance >= GNOSIS_GAS.minWei) return;
  onProgress?.("Topping up processing fuel…");
  await refillGnosisGas(wallet, GNOSIS_GAS.topUpUnits);
}

/**
 * Convert an exact slice of a fresh deposit into native xDAI. Unlike
 * ensureGnosisGas this always runs: the bootstrap advance deliberately makes
 * the pre-refill balance non-zero, then this purchase repays that advance and
 * leaves the user's own long-lived gas reserve.
 */
export async function refillGnosisGas(
  wallet: LocalWallet,
  units: bigint,
  onPrepared?: (transaction: PreparedLifiTransaction) => Promise<void> | void,
): Promise<`0x${string}`> {
  const quote = await getLifiQuote({
    fromChain: GNOSIS_CHAIN.id,
    toChain: GNOSIS_CHAIN.id,
    fromToken: RESERVE_TOKEN.address,
    toToken: "native",
    fromAmount: units,
    fromAddress: wallet.address,
    slippage: LIFI.bridgeSlippage,
  });
  const txHash = await sendRecoverableLifiTransaction(
    wallet.account,
    quote,
    onPrepared ?? (() => undefined),
  );
  await waitLifiDone(txHash, GNOSIS_CHAIN.id, GNOSIS_CHAIN.id);
  return txHash;
}

/**
 * For user-driven 4663 transactions outside the batch pipeline (cash-out
 * sells): make sure the wallet can pay gas, refilling a standard slice from
 * the reserve. Throws a clear error instead of letting the sell tx fail
 * opaquely with "insufficient funds for gas".
 */
export async function ensureDestGasForSell(wallet: LocalWallet, onProgress?: (label: string) => void): Promise<void> {
  const balance = await destEthBalance(wallet);
  if (balance >= DEST_GAS.minWei) return;
  onProgress?.("Topping up Robinhood Chain gas…");
  try {
    await refillDestGas(wallet, DEST_GAS.topUpUnits);
  } catch (err) {
    throw new Error(
      "The wallet has no ETH on Robinhood Chain to pay for the sale, and " +
        `refilling from the reserve failed: ${err instanceof Error ? err.message : String(err)}. ` +
        "Top up the reserve (USDC.e on Gnosis) and try again.",
    );
  }
}
